import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import AlertTitle from '@mui/material/AlertTitle';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DoneIcon from '@mui/icons-material/Done';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { toWapi, post } from '../../util/api';

import Divider from '@mui/material/Divider';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Skeleton from '@mui/material/Skeleton';
import StepOneImage from './images/1.png';
import StepTwoImage from './images/2.png';
import AccessImageOne from './images/mcaccess.png'
import AccessImageTwo from './images/mcaccess2.png'
import StepThreeImage from './images/3.png';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/system/Box';

function Googlesheets(props) {
	const [spreadSheetID, setSpreadSheetID] = useState('');
	const [selectBoxOptions, setSelectBoxOptions] = useState([]);
	const [copiedPid, setCopiedPid] = useState(false);
	const [email, setEmail] = useState('google-sheets-message-sender@integrations-414709.iam.gserviceaccount.com');
	const [inputsCompleted, setInputsCompleted] = useState(false);
	const [processSuccessful, setProcessSuccessful] = useState();
	const [selectedOption, setSelectedOption] = useState('');

	useEffect(() => {
		const { product } = props;
		const {
			id,
			settings: { wapi_token },
		} = product;

		toWapi(`/api/${id}/listPhones`, wapi_token)
			.then((data) => {
				if (data.status !== 200) {
					return;
				}
				if (Array.isArray(data.data)) {
					const ids = _.map(data.data, 'id');
					setSelectBoxOptions(ids);
				} else {
					console.log('Data, bir dizi içermiyor.');
				}
			})
			.catch((err) => {
				console.error(err);
			});
	}, []);

	useEffect(() => {
		if (spreadSheetID !== '' && selectBoxOptions.length > 0 && email !== '') {
			setInputsCompleted(true);
		} else {
			setInputsCompleted(false);
		}
	}, [spreadSheetID, selectBoxOptions, email]);

	const extractSpreadSheetID = (spreadsheetLink) => {
		const regex = /\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/;
		const match = spreadsheetLink.match(regex);
		return match ? match[1] : null;
	};

	const handleGoogleSheetSendMessage = async () => {
		const { product } = props;
		const {
			id,
			settings: { wapi_token },
		} = product;
		setProcessSuccessful(null);
		const extractID = extractSpreadSheetID(spreadSheetID);

		try {
			const response = await post('/googlesheets/sheetsender', {
				spreadsheetId: extractID,
				production_id: id,
				token: wapi_token,
				phoneID: selectedOption,
				wapi_token: wapi_token,
			});
			console.log(response);
			setProcessSuccessful(true);
		} catch (error) {
			console.log(error);
			setProcessSuccessful(false);
		}
	};

	const onCopyPid = () => {
		setCopiedPid(true);
	};
	const handleNewProcess = () => {
		window.location.reload();
	};

	const onDownload = () => {
		const link = document.createElement('a');
		link.download = 'MaytapiExampleSheetsFile.xlsx';
		link.href = '/MaytapiExampleSheetsFile.xlsx';
		link.click();
	};

	return (
		<Stack>
			<Typography variant="h5" gutterBottom component="div">
				Google Sheets Message Sender
				<Divider sx={{ mt: 1 }} />
			</Typography>
			<Stack>
				<Typography variant="body1" gutterBottom>
					"Google Sheets Message Sender" is a tool for streamlined communication via Google Sheets. It lets users send messages, notifications, or
					alerts to predefined recipients directly from their Sheets interface. Ideal for team updates, event reminders, or group announcements, it
					offers a seamless way to manage communication tasks within Google Sheets.
				</Typography>
				<Stack mb={2}>
					<Typography sx={{ display: 'flex' }} variant="h6" gutterBottom>
						Example File Template
					</Typography>
					<Typography variant="body2" gutterBottom>
						Your Google Sheets file should follow a specific format to work with our system. It includes columns for message type, recipient number, content, and additional options for media and poll messages. For detailed instructions on how to structure your spreadsheet, please refer to the "How should we use it?" section below.
					</Typography>
					<Accordion sx={{ marginTop: '10px' }}>
						<AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel4-content" id="panel4-header">
							<Typography sx={{ fontWeight: 'bold' }}>Which message types do we support?</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>
								The types of messages we currently support:
								<ul>
									<li>Text messages</li>
									<li>Media messages (image, video, PDF)</li>
									<li>Base64 media messages (image, video, PDF)</li>
									<li>Poll messages</li>
								</ul>
								For more information on how to use each type, please refer to the “How should we use it?” section.
							</Typography>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel5-content" id="panel5-header">
							<Typography sx={{ fontWeight: 'bold' }}>How should we use it?</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>
								To send a message using Google Sheets:
								<ol>
									<li>In your Google Sheets file, type the message type (text, media, or poll) in column A.</li>
									<li>Write the recipient's phone number in column B.</li>
									<li>In column C, type the message or media URL to send.</li>
									<li>If you are sending a media message, you can add an optional title in column D.</li>
									<li>For poll messages, type the poll options in columns D, E and F. (We currently support sending a maximum of 3 options).</li>
									<li>For polls, you can ensure that only one selection is made by entering TRUE in column G.</li>
								</ol>
								An example line:
								<pre>
									media | 12345678901 | https://example.com/image.jpg | Test Message
								</pre>
								<Alert severity="info" sx={{ mt: 2, mb: 2 }}>
									<AlertTitle>Important Note for Base64 Media Messages:</AlertTitle>
									If you want to send a media message using base64 encoding, you must prefix the base64 string with the appropriate MIME type. For example:
									<pre>
										data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAIBAQIBAQICAgIC...
									</pre>
									Common MIME types:
									<ul>
										<li>JPEG image: data:image/jpeg;base64,</li>
										<li>PNG image: data:image/png;base64,</li>
										<li>PDF document: data:application/pdf;base64,</li>
									</ul>
								</Alert>
							</Typography>
						</AccordionDetails>
					</Accordion>
					<Button sx={{marginTop:'10px'}} startIcon={<DownloadIcon />} onClick={onDownload} variant="contained" color="primary">
						Download
					</Button>
				</Stack>
				<Alert severity="warning">Complete the following steps correctly for a smooth process.</Alert>
				<Stack>
					<Accordion>
						<AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel1-content" id="panel1-header">
							<Typography>Step 1 : Select Active Phone</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>
								In the phone selection field in the first step of the form, you must select the phone you use and send messages to. These
								messages to be sent will be sent via the phone you have selected.
							</Typography>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel2-content" id="panel2-header">
							<Typography>Step 2 : Complete permissions</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>
								The mail section in the second step of the form will write to inform you about the message status of the messages after sending
								them
								<br />
								for this From the Share tab on the Google sheet page, give "write and read" permission to the e-mail address you copied
							</Typography>
							<Divider />
							<Typography my={2}>If you don't understand, you can follow the pictures and perform the permission process.</Typography>
							<Stack>
								<Box>
									<div> Press the Share button at the top of the page </div>

									<img width={'600px'} src={StepOneImage}></img>
								</Box>
								<Box>
									<div> In the Modal that opens, take the copied mail and paste it </div>
									<img width={'600px'} src={StepTwoImage}></img>
								</Box>


								<Box>
									<div>After pasting, authorize as "Editor" and press the Send button</div>
									<img width={'600px'} src={StepThreeImage}></img>
								</Box>

								<Box>
									<div>then change the "Restricted" feature under the "General access" heading at the bottom to "Anyone with the link"</div>
									<img width={'400px'} src={AccessImageOne}></img>
								</Box>

								<Box>
									<div>it will take a view as it appears below, if it appears in a different way, change it to "Viewer" in Options</div>
									<img width={'400px'} src={AccessImageTwo}></img>
								</Box>
								<Box>
									Congratulations 🎉 You have completed the steps related to permissions, proceed to the next step
								</Box>
							</Stack>
						</AccordionDetails>
					</Accordion>
					<Accordion>
						<AccordionSummary expandIcon={<ArrowDropDownIcon />} aria-controls="panel3-content" id="panel3-header">
							<Typography>Step 3 : Share your link </Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Typography>
								In Step 3, you need to put your existing Google Sheets Address in the field in the form
								<Alert severity="info">https://docs.google.com/spreadsheets/d/YOURLINK/edit#gid=0</Alert>
								You have completed all the steps, Congratulations. If you are sure that everything is correct, you can press the Submit button
							</Typography>
						</AccordionDetails>
					</Accordion>
				</Stack>
			</Stack>
			<Stack>
				<Stack mt={2}>
					<TextField
						select
						label="Enter the active phone"
						value={selectedOption}
						onChange={(e) => setSelectedOption(e.target.value)}
						variant="outlined"
					>
						{selectBoxOptions.map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</TextField>

					<TextField
						label="Mail to be authorised"
						sx={{ marginTop: '10px', marginBottom: '10px' }}
						fullWidth
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						variant="outlined"
						action
						fluid
						InputProps={{
							readOnly: true,
							endAdornment: (
								<CopyToClipboard text={email} onCopy={onCopyPid}>
									<Button
										sx={{ borderRadius: '4px' }}
										color={copiedPid ? 'success' : undefined}
										size="medium"
										margin="normal"
										variant="contained"
										endIcon={copiedPid ? <DoneIcon /> : <ContentCopyIcon />}
									>
										{copiedPid ? 'Copied' : 'Copy'}
									</Button>
								</CopyToClipboard>
							),
						}}
					/>

					<TextField label="Your Google Sheets Address" variant="outlined" value={spreadSheetID} onChange={(e) => setSpreadSheetID(e.target.value)} />
					<Button variant="contained" sx={{ marginTop: '10px' }} onClick={handleGoogleSheetSendMessage} disabled={!inputsCompleted}>
						Send Message with Google sheets
					</Button>
					<Stack>
						{processSuccessful === null && (
							<Stack alignItems="center" justifyContent="center" mt={2} spacing={1}>
								<Skeleton variant="circular" width={50} height={50} />
								<Skeleton variant="rectangular" width={500} height={60} />
								<Skeleton variant="rectangular" width={600} height={30} />
							</Stack>
						)}
						{processSuccessful === true && (
							<Stack direction="column" alignItems="center" mt={2} spacing={1}>
								<CheckCircleIcon sx={{ fontSize: 50 }} color="success" />
								<Typography variant="h4" color="success">
									Process Successful
								</Typography>
								<Typography textAlign="center" variant="body1">
									The transaction was successful. Message statuses will be reflected in the table in real time
								</Typography>
								<Button variant="contained" color="success" sx={{ marginTop: '10px' }} onClick={handleNewProcess}>
									Start New Process
								</Button>
							</Stack>
						)}
						{processSuccessful === false && (
							<Stack direction="column" alignItems="center" mt={2} spacing={1}>
								<CancelIcon sx={{ fontSize: 50 }} color="error" />
								<Typography variant="h4" color="error">
									An error occurred during the process.
								</Typography>
								<Typography sx={{ width: '50%' }} textAlign="center" variant="body1">
									An error occurred while processing the spreadsheet. Please check if you have selected the Google Sheets Link and the active
									phone and try again.
								</Typography>
								<Button variant="contained" color="error" sx={{ marginTop: '10px' }} onClick={handleNewProcess}>
									Start New Process
								</Button>
							</Stack>
						)}
					</Stack>
				</Stack>
			</Stack>
		</Stack>
	);
}

const stateToProps = (state) => ({
	access: state.access,
	location: state.router.location,
	product: state.product.maytapi,
});

export default connect(stateToProps)(Googlesheets);
